import { cn } from './cn';
import { HottrOrb } from './HottrOrb';
import type { HottrProduct } from './worlds';
import { WORLDS } from './worlds';

/**
 * HottrLockup — the master lockup, for every product in the ecosystem.
 *
 *     [WORLD ICON]  HOTTR.<suffix>
 *
 * One component renders all thirteen. That is the governance mechanism, not a
 * convenience: the suffix rule (Poppins Light 300, lowercase, roman, 70% white,
 * zero space after the fullstop) is ecosystem law, and the reliable way to keep
 * thirteen product teams from each "improving" it is to give them no place to.
 * Only three things change per product — suffix, icon, accessible label.
 *
 * Prohibited, and impossible through this component (design.md §5.3):
 * script or italic suffixes, per-product typefaces, coloured suffixes, the Orb
 * replacing the fullstop, the icon between HOTTR and its suffix, and any glow,
 * bevel or texture on the wordmark. The icon may be expressive. The wordmark
 * may not.
 *
 * Sizing is set in one place — the whole lockup scales from a single font-size
 * so the icon, cap height and suffix stay in proportion at every size.
 */

const SIZES = {
  xs: { text: 'text-[14px]', icon: 14, gap: 'gap-1.5' },
  sm: { text: 'text-[18px]', icon: 18, gap: 'gap-2' },
  md: { text: 'text-[24px]', icon: 24, gap: 'gap-2.5' },
  lg: { text: 'text-[32px]', icon: 32, gap: 'gap-3' },
  xl: { text: 'text-[48px]', icon: 46, gap: 'gap-4' },
} as const;

export type LockupSize = keyof typeof SIZES;

export interface HottrLockupProps {
  product: HottrProduct;
  size?: LockupSize;
  /** Show the product icon to the left of the wordmark. */
  showIcon?: boolean;
  /** Overrides the announced name. Defaults to "HOTTR.<suffix>". */
  accessibleLabel?: string;
  className?: string;
}

export const HottrLockup = ({
  product,
  size = 'md',
  showIcon = true,
  accessibleLabel,
  className,
}: HottrLockupProps) => {
  const { suffix } = WORLDS[product];
  const s = SIZES[size];
  const label = accessibleLabel ?? `HOTTR.${suffix}`;

  return (
    <span
      className={cn('inline-flex items-center whitespace-nowrap', s.gap, s.text, className)}
      role="img"
      aria-label={label}
    >
      {/* The Orb sits to the LEFT as a separate asset. It never touches the
          wordmark and never replaces the fullstop below (§02) — that fullstop
          is a real typographic character, flat and unanimated, while the Orb
          beside it may glow and breathe.

          Per-product glyphs are parked, so every product shows the same Orb —
          but `product` still does something: it shifts the Orb's light source
          (--orb-highlight-x/y), so HOTTR.pro is lit from above like a spotlight
          and HOTTR.care from lower and softer. Same object, different light.

          Small lockups get the Core Orb, which is flat by design (§7.1). */}
      {showIcon ? (
        <HottrOrb
          size={s.icon}
          level={s.icon <= 32 ? 'core' : 'living'}
          product={product}
        />
      ) : null}

      <span aria-hidden="true" className="inline-flex items-baseline font-display leading-none">
        {/* Poppins Black 900, uppercase, white, tracking -0.04em. Never restyled. */}
        <span className="font-black uppercase tracking-hottr-display text-white">HOTTR</span>
        {/* The fullstop is flat accent — the brand's atomic unit. Never white, never inherited. */}
        <span className="font-black text-hottr-pink">.</span>
        {/* Zero added space after the fullstop: the suffix hangs straight off it. */}
        <span className="hottr-suffix font-light">{suffix}</span>
      </span>
    </span>
  );
};

export default HottrLockup;
